export const ARTICLES = {
  "/updates/why-were-building-real-time-ai-video-companions": {
    title: "Why We're Building Real-Time AI Video Companions",
    metaTitle: "Why We're Building Real-Time AI Video Companions — AIROBOTCAMSTUDIO Journal",
    metaDesc: "Why AIROBOTCAMSTUDIO is moving AI companionship beyond text: real-time video, voice and photorealistic AI characters. MVP currently in development.",
    date: "29 August 2026",
    readTime: "6 min read",
    related: [
      ["Inside Our Real-Time AI Videochat MVP", "/updates/inside-our-real-time-ai-videochat-mvp"],
      ["Why Photorealistic AI Avatars Matter", "/updates/why-photorealistic-ai-avatars-matter"],
      ["Technology: Real-Time AI Video", "/technology/real-time-ai-video"],
    ],
    subtitle: "The thinking behind moving AI companionship from text boxes to live, face-to-face interaction.",
    intro:
      "Most AI companion experiences today are primarily text or image based. We believe the next step is presence — a character you can see and hear, that responds in the moment. This article explains why.",
    sections: [
      {
        heading: "Beyond text",
        paragraphs: [
          "Text-based AI companions have shown that people want conversation, personality and continuity from AI. But a chat window is a narrow channel. Tone of voice, facial expression, timing and eye contact carry most of what makes an interaction feel real, and none of that fits inside a text box.",
          "Our vision is a more immersive experience that combines four layers into one: real-time video, voice, conversational AI and photorealistic characters. Each layer exists today in separate products. Bringing them together into a single, coherent experience is the problem AIROBOTCAMSTUDIO exists to solve.",
        ],
      },
      {
        heading: "Presence changes everything",
        paragraphs: [
          "When a character is visible on camera, small details start to matter: a pause before answering, a smile that arrives at the right moment, movement that feels natural rather than animated. These are the qualities we are designing for from the start, rather than adding them later.",
          "This is also why we describe AIROBOTCAMSTUDIO as sitting at the intersection of artificial intelligence, interactive entertainment, real-time video and virtual companions. It is not a chatbot company with a video feature; the video presence is the product.",
        ],
      },
      {
        heading: "What we are building first",
        paragraphs: [
          "The first product milestone is a working real-time AI videochat experience with photorealistic AI characters. It is currently in development — the commercial product has not launched, and the visuals and demonstrations on this website are clearly labelled as concepts and development material.",
          "The intended experience is simple to describe: see the character, hear the character, talk naturally, and have the character respond with expression, emotion and movement. Getting those four things to work together convincingly is the entire engineering challenge, and we would rather build it carefully than announce it loudly.",
        ],
      },
      {
        heading: "What we deliberately avoid",
        paragraphs: [
          "You will not find invented metrics, mock testimonials or claims of a finished product on this website. Where something is a concept, we label it a concept. Where something is in development, we say it is in development. We think this is simply what a serious technology company should do, and it makes the eventual launch more credible, not less.",
        ],
      },
      {
        heading: "Why now",
        paragraphs: [
          "Conversational AI, voice synthesis and real-time generative video have each matured quickly, and we believe the tools are now becoming available to build what was not practical a few years ago. A small, focused company can attempt this today without pretending the hard parts are solved — which is exactly how we intend to work.",
        ],
      },
      {
        heading: "How the pieces fit together",
        paragraphs: [
          "The architecture we are building towards follows a clear pipeline: user input flows into the AI layer, the AI drives the voice, the voice and intelligence drive the character, and the character is presented through real-time video. Each stage must stay fast enough that the whole chain still feels like a conversation.",
          "That is why our MVP priorities are deliberately unglamorous: latency, realism and session stability. We also keep infrastructure cost per streamed minute visible from the start — a beautiful experience that cannot be operated economically is not a product.",
        ],
      },
      {
        heading: "Honest about what AI characters are",
        paragraphs: [
          "Our characters are simulated personalities: responsive, expressive and interactive by design, but not conscious and not capable of genuine emotion. We think this honesty makes the product better — users deserve to know exactly what they are interacting with, and the experience can be compelling without pretending otherwise.",
        ],
      },
      {
        heading: "Where this leads",
        paragraphs: [
          "In the longer term, our ambition is to bring the same AI characters and intelligence beyond the screen — ultimately into humanoid robots. That is the future vision, not the current product. Today, our focus is firmly on the digital experience: real-time video, voice and conversation.",
        ],
      },
    ],
  },

  "/updates/inside-our-real-time-ai-videochat-mvp": {
    title: "Inside Our Real-Time AI Videochat MVP",
    metaTitle: "Inside Our Real-Time AI Videochat MVP — AIROBOTCAMSTUDIO Journal",
    metaDesc: "A look inside the AIROBOTCAMSTUDIO MVP: a real-time AI videochat experience with photorealistic AI characters, currently in development.",
    date: "29 August 2026",
    readTime: "7 min read",
    related: [
      ["Why We're Building Real-Time AI Video Companions", "/updates/why-were-building-real-time-ai-video-companions"],
      ["Level 1 — Real-Time Videochat MVP", "/roadmap/real-time-videochat-mvp"],
      ["Our Roadmap to Interactive AI Entertainment", "/updates/our-roadmap-to-interactive-ai-entertainment"],
    ],
    intro:
      "Our first product milestone is a working real-time AI videochat experience with photorealistic AI characters. Here is what the MVP is intended to include, and an honest view of where development stands.",
    sections: [
      {
        heading: "What the MVP is",
        paragraphs: [
          "The MVP is the smallest version of the product that delivers the core experience: a live, visual conversation with a photorealistic AI character. It combines an AI avatar, live video, voice, conversation, facial expressions, emotional responses and natural movement into a single session.",
          "Everything else — subscriptions, premium interactive experiences, live AI entertainment, premium characters and future business-to-business opportunities — belongs to the commercial platform that would follow. Those are potential future revenue models, not current ones; AIROBOTCAMSTUDIO LTD does not currently generate revenue and has no customers.",
        ],
      },
      {
        heading: "The intended experience: See, Hear, Feel, Engage",
        paragraphs: [
          "We describe the intended experience in four words. See: a photorealistic character on real-time video. Hear: natural voice and conversational intelligence. Feel: expression, movement and persistent personality. Engage: measurable one-to-one interaction.",
          "If those four things work together convincingly, the experience crosses from reading about a character to being with one. That is the bar the MVP is being built against — and it is why we treat latency, expression quality and conversational coherence as one problem rather than three.",
        ],
      },
      {
        heading: "The full Level 1 scope",
        paragraphs: [
          "Level 1 is the current commercial build, and it covers more than the conversation itself. Alongside the photorealistic AI character with real-time video, natural voice, conversational AI, facial reactions and movement, the scope includes the practical machinery a real product needs: signup, a demo, subscriptions, payments, beta analytics, moderation and feedback.",
          "None of these components is complete yet — each carries its own development status, and the SEIS fundraising round is focused on completing and validating exactly this level.",
        ],
      },
      {
        heading: "What 'in development' means",
        paragraphs: [
          "The MVP is currently in development. The characters shown on this website are AI-generated and entirely fictional concept characters, and the videochat interfaces shown are concept visuals that represent the planned experience. Our Technology Demo videos are labelled as pre-rendered concept demonstrations, and the real-time MVP is not yet operational.",
          "None of this should be read as a commercially live product. When real demonstrations of working technology exist, they will be published here and clearly identified as such.",
        ],
      },
      {
        heading: "How we will show progress",
        paragraphs: [
          "The Technology Demo section of this website is where demonstrations will appear as development progresses — covering real-time AI videochat, photorealistic AI characters, voice interaction, emotional expression and natural movement. Each piece of material is labelled as a concept or development demonstration until it shows working product.",
          "This Journal will carry honest updates on the same basis: what was built, what worked, and what is next.",
        ],
      },
      {
        heading: "What success looks like",
        paragraphs: [
          "The MVP succeeds when a session feels like a conversation rather than a demonstration: responses arrive in time to feel natural, expressions match the tone of what is being said, and movement stays believable throughout. Those are the qualities our testing will measure before we show the product publicly.",
        ],
      },
      {
        heading: "How progress will be measured",
        paragraphs: [
          "Validation is part of the build, not an afterthought. The measures that matter for Level 1 are latency, realism and session stability first, then — once real users are involved — retention, paid usage and willingness to pay. These are validation objectives: none of these metrics exist yet, and we will not quote them until they do.",
          "The commercial model will be defined with the same discipline: commercial pricing has not been finalised, and any prices or packages would be set only after the MVP, informed by real user behaviour rather than assumption.",
        ],
      },
      {
        heading: "What comes after",
        paragraphs: [
          "Once the MVP works, the next chapter is testing: a recorded preview, then interactive previews, then — when the product is ready — a public beta. You can register your interest now to receive future updates about testing opportunities.",
        ],
      },
    ],
  },

  "/updates/why-photorealistic-ai-avatars-matter": {
    title: "Why Photorealistic AI Avatars Matter",
    metaTitle: "Why Photorealistic AI Avatars Matter — AIROBOTCAMSTUDIO Journal",
    metaDesc: "Why AIROBOTCAMSTUDIO builds photorealistic, fictional AI characters rather than cartoon avatars — believability, expression and presence on camera.",
    date: "29 August 2026",
    readTime: "6 min read",
    related: [
      ["Technology: Photorealistic AI Characters", "/technology/photorealistic-ai-characters"],
      ["Why We're Building Real-Time AI Video Companions", "/updates/why-were-building-real-time-ai-video-companions"],
      ["Inside Our Real-Time AI Videochat MVP", "/updates/inside-our-real-time-ai-videochat-mvp"],
    ],
    intro:
      "The difference between a character you tolerate and a character you talk to is believability. Here is why we chose photorealistic digital humans over stylised avatars.",
    sections: [
      {
        heading: "Believability is the product",
        paragraphs: [
          "A companion experience lives or dies on whether the character feels present. Cartoon avatars and stylised game characters create distance: they signal, constantly, that you are looking at a graphic. A photorealistic character removes that barrier and lets the conversation carry the experience.",
          "That is why our concept characters — Aria, Nova, Selene and Iris — are presented with realistic facial features, natural lighting and cinematic framing, the way a person would appear on a well-lit video call.",
          "Each character is framed with the discipline a photographer would use — consistent lighting, consistent framing, nothing that breaks the sense that this is a person on the other side of a camera.",
        ],
      },
      {
        heading: "Expression, not just answers",
        paragraphs: [
          "The goal is not a face that reads out responses. The planned experience combines natural voice, emotional reactions, facial expressions and realistic movement, so the character reacts the way a person would — with timing, emphasis and visible feeling.",
          "This is harder to build than text generation, and it is exactly why we think it is worth building. Personality in a companion is carried as much by expression as by words.",
        ],
      },
      {
        heading: "The interface deserves the same care",
        paragraphs: [
          "The videochat interfaces shown on this website are concept visuals, and they are designed with the same intent as the characters themselves: premium, calm and cinematic. If the character is believable but the frame around it feels cheap, the illusion breaks. Presentation is part of the product.",
          "That is also why concept material is labelled as concept material — the standard applies to honesty as much as to imagery.",
        ],
      },
      {
        heading: "Fictional by design",
        paragraphs: [
          "Every character on this website is AI-generated and entirely fictional, and each is clearly labelled as such. They are original concept characters created for AIROBOTCAMSTUDIO — not depictions of real people, and not intended to resemble any real person.",
          "Clear labelling is a deliberate choice. As AI-generated humans become more convincing, transparency about what is and is not real becomes part of the product's integrity, not a footnote.",
        ],
      },
      {
        heading: "Different characters, different personalities",
        paragraphs: [
          "Photorealism alone is not enough — the characters also need distinct personalities. Aria is warm, witty and endlessly curious; Nova has playful energy and a sharp sense of humour; Selene is calm, sophisticated and quietly perceptive; Iris is bold and adventurous. Appearance draws you in, but personality is what you come back for.",
        ],
      },
      {
        heading: "Identity that persists",
        paragraphs: [
          "A companion you return to needs to be the same companion. Persistent identity — the same face, the same voice, the same personality across sessions — is what turns a one-off novelty into an ongoing experience. That continuity is designed into how we think about characters from the start, and it is why each concept character has a name, a look and a temperament rather than a generic avatar slot.",
          "It is worth repeating what that persistence is and is not: the character's personality is a consistent, designed simulation. It does not imply memory beyond what the product provides, and it never implies consciousness.",
        ],
      },
      {
        heading: "Where this fits in the experience",
        paragraphs: [
          "Photorealistic characters are the See layer of the four-part experience we are building — See, Hear, Feel, Engage. They are the visible surface that voice, expression and interaction attach to, which is why so much of our early development effort sits here: if the character on screen is not believable, nothing layered on top of it can rescue the experience.",
        ],
      },
      {
        heading: "Built for presence",
        paragraphs: [
          "Photorealism is a means to an end: presence. The character should hold your attention the way a person on camera does — and that standard guides every visual decision we make as development of the MVP continues.",
          "We would rather present four characters built to that standard than forty that fall short of it.",
        ],
      },
    ],
  },

  "/updates/from-digital-ai-characters-to-humanoid-ai": {
    title: "From Digital AI Characters to Humanoid AI",
    metaTitle: "From Digital AI Characters to Humanoid AI — AIROBOTCAMSTUDIO Journal",
    metaDesc: "The long-term AIROBOTCAMSTUDIO vision: extending digital AI characters beyond the screen into humanoid robots. Software first — robotics is the future, not the current product.",
    date: "29 August 2026",
    readTime: "6 min read",
    related: [
      ["Humanoid AI — Long-Term Vision", "/roadmap/humanoid-ai"],
      ["Our Roadmap to Interactive AI Entertainment", "/updates/our-roadmap-to-interactive-ai-entertainment"],
      ["Why Photorealistic AI Avatars Matter", "/updates/why-photorealistic-ai-avatars-matter"],
    ],
    intro:
      "Our roadmap ends with an ambitious idea: bringing the same AI characters and intelligence into physical humanoid robots. This article explains how we think about that vision — and why it is chapter four, not chapter one.",
    sections: [
      {
        heading: "Software first",
        paragraphs: [
          "AIROBOTCAMSTUDIO is an AI entertainment company. The current product in development is a real-time AI videochat experience with photorealistic virtual companions — software, on a screen, in a conversation. That is where our development effort goes today.",
          "You can see that ordering across the website: robotics appears as one chapter on the roadmap and one short note in the future vision — never as the headline.",
          "The progression we describe is deliberate: digital AI characters, then interactive real-time experiences, then a commercial platform, and only then anything physical.",
        ],
      },
      {
        heading: "The longer-term vision",
        paragraphs: [
          "The ambition is that a character you have built a rapport with on screen should not be trapped there. If the intelligence, personality and memory of an AI companion can live in software, the same identity could one day be extended into a physical humanoid form.",
          "Digital AI to interactive AI to humanoid AI — each stage builds the characters and intelligence the next stage would need.",
        ],
      },
      {
        heading: "What stays constant across every stage",
        paragraphs: [
          "The character is the constant. The name, the personality, the conversational style and the relationship a user builds should survive every transition — from a video window, to richer interactive formats, and ultimately, if the vision is realised, to a physical presence. The container changes; the identity does not.",
          "That continuity is the real engineering bet: build a character worth keeping, and every future form factor becomes an opportunity rather than a reset.",
        ],
      },
      {
        heading: "Why robotics is chapter four",
        paragraphs: [
          "Humanoid robotics is capital-intensive, technically unforgiving and dependent on capabilities that do not exist in our product yet. Placing it at the end of the roadmap is not caution for its own sake; it reflects the honest order in which these capabilities must be built and proven.",
          "A company that leads with robots before it has a working digital product is telling the story backwards.",
        ],
      },
      {
        heading: "A note on patience",
        paragraphs: [
          "Future robotics research and development appears in our long-term plans — but it is deliberately the most distant commitment, and it is no part of the current fundraising round. We would rather arrive at humanoid AI late with a proven digital product than early with a promise we cannot keep.",
          "The characters themselves will not wait for hardware. Everything we build digitally — personality, conversation, presence — is designed to carry forward, whatever form the distant future takes.",
        ],
      },
      {
        heading: "What the current fundraising funds",
        paragraphs: [
          "To remove any doubt: the proposed £150,000 round and the SEIS Advance Assurance application relate exclusively to AIROBOTCAMSTUDIO LTD's digital AI video companion platform and directly related product development. No SEIS capital is allocated to purchasing, manufacturing or researching humanoid robots.",
          "AIROBOTCAMSTUDIO is not currently selling or manufacturing humanoid robots, and no finished physical robot product exists. Any future robotics work would be subject to separate planning and separate funding.",
        ],
      },
      {
        heading: "What would have to come first",
        paragraphs: [
          "Before humanoid AI could become more than a vision, the digital platform would need to work, be validated by real users and sustain itself commercially. That sequence — Level 1, richer interaction, advanced interaction, commercial platform — exists precisely so that each capability is proven before the next is attempted.",
        ],
      },
      {
        heading: "What we are not claiming",
        paragraphs: [
          "We are not a robotics company today, we are not building humanoid hardware today, and nothing on this website should be read as a near-term robotics product. The humanoid vision is a long-term direction — visible on our roadmap, and clearly secondary to the platform now in development.",
        ],
      },
    ],
  },

  "/updates/our-roadmap-to-interactive-ai-entertainment": {
    title: "Our Roadmap to Interactive AI Entertainment",
    metaTitle: "Our Roadmap to Interactive AI Entertainment — AIROBOTCAMSTUDIO Journal",
    metaDesc: "The AIROBOTCAMSTUDIO roadmap: the Level 1 real-time videochat MVP, richer and more advanced interaction, the commercial platform, and the long-term humanoid AI vision.",
    date: "29 August 2026",
    readTime: "6 min read",
    related: [
      ["Inside Our Real-Time AI Videochat MVP", "/updates/inside-our-real-time-ai-videochat-mvp"],
      ["From Digital AI Characters to Humanoid AI", "/updates/from-digital-ai-characters-to-humanoid-ai"],
      ["Commercial Platform — Roadmap", "/roadmap/commercial-platform"],
    ],
    intro:
      "Our roadmap moves through clear stages, each building on the last. Here is what each one means, and where things stand today.",
    sections: [
      {
        heading: "Level 1 — Real-Time Videochat MVP",
        paragraphs: [
          "The current commercial build: a live photorealistic character with natural voice, facial response and real-time video. The intended experience is simple — see, hear, feel, engage — and deliberately narrow in scope so it can be excellent.",
          "Status: in development. The commercial product has not launched, and demonstrations on this website are labelled as concept material until they show working product.",
          "Success is measured by how a session feels: timely responses, expressions that match tone, and movement that stays natural throughout.",
        ],
      },
      {
        heading: "Level 2 — Richer interaction",
        paragraphs: [
          "Once Level 1 is working and validated, Level 2 deepens it: user-triggered actions, richer reactions and sessions that go beyond question-and-answer conversation. The character does more than converse.",
          "Status: next. This stage has not been built, and it begins only after the Level 1 MVP is validated with real users.",
        ],
      },
      {
        heading: "Level 3 — Advanced interaction",
        paragraphs: [
          "Advanced movement, environment interaction and premium formats — the stage where interaction becomes visibly richer and higher-value session types become possible.",
          "Status: future. It follows Level 2 and depends on everything before it working.",
        ],
      },
      {
        heading: "Commercial platform",
        paragraphs: [
          "The proposed path runs: MVP, validation, paid usage validation, controlled acquisition, and then a commercial platform. The go-to-market principle is to launch narrow and measure before scaling: a closed beta, then conversion from early access to paid usage, then retention, and paid acquisition only once acquisition cost and retention are measurable.",
          "The potential revenue models we are exploring include subscriptions, premium interactive experiences, live AI entertainment, premium AI characters and future business-to-business opportunities. These are potential future models only — AIROBOTCAMSTUDIO LTD does not currently generate revenue and has no customers. Status: future.",
        ],
      },
      {
        heading: "Humanoid AI",
        paragraphs: [
          "Extend the AI characters and intelligence into physical humanoid robots. This is the long-term vision — deliberately last, and clearly separated from the product in development today. SEIS capital is allocated exclusively to the digital AI video companion platform and directly related product development. Status: long-term vision.",
          "We include it openly because it shapes how we design characters today: as identities built to last, not interfaces built to be replaced.",
        ],
      },
      {
        heading: "Why staged, not one launch",
        paragraphs: [
          "Sequencing is a form of honesty. The MVP must work before richer interaction makes sense; each level must be validated before a commercial platform makes sense; and the platform must succeed before humanoid ambitions deserve serious investment. Each stage removes risk from the next.",
          "It also keeps our public statements easy to check: every stage has an honest status, all visible on the roadmap section of this site.",
        ],
      },
      {
        heading: "How to read this roadmap",
        paragraphs: [
          "Each chapter has an honest status, and we will keep those statuses accurate as work progresses. The progression — AI characters, real-time interaction, commercial platform, humanoid AI — is not a marketing timeline; it is the order in which the capabilities genuinely depend on each other.",
          "When something moves from 'in development' to 'built', you will read it here first, in plain language.",
          "Roadmaps are easy to draw and hard to keep. Ours is short enough to keep honestly.",
        ],
      },
    ],
  },

  "/updates/preparing-airobotcamstudio-for-investment": {
    title: "Preparing AIROBOTCAMSTUDIO for Investment",
    metaTitle: "Preparing AIROBOTCAMSTUDIO for Investment — AIROBOTCAMSTUDIO Journal",
    metaDesc: "AIROBOTCAMSTUDIO LTD is preparing a £150,000 fundraising round and a future Crowdcube campaign. What the funds are for, and what we are not claiming.",
    date: "29 August 2026",
    readTime: "6 min read",
    related: [
      ["Understanding Our SEIS Advance Assurance Preparation", "/updates/understanding-our-seis-advance-assurance-preparation"],
      ["SEIS Advance Assurance — Investment", "/investment/seis-advance-assurance"],
      ["Inside Our Real-Time AI Videochat MVP", "/updates/inside-our-real-time-ai-videochat-mvp"],
    ],
    intro:
      "AIROBOTCAMSTUDIO LTD is preparing to raise £150,000 to accelerate development of its real-time AI videochat MVP and commercial platform. Here is what that preparation involves, stated plainly.",
    sections: [
      {
        heading: "What we are preparing",
        paragraphs: [
          "The planned £150,000 round is focused entirely on completing, validating and launching the digital AI video companion platform and directly related product development. Robotics research and development belongs to a later stage of the company and is not part of this round.",
          "The status today is fundraising preparation. We are seeking investment and registering expressions of interest — nothing more.",
        ],
      },
      {
        heading: "The proposed round in numbers",
        paragraphs: [
          "Target raise: £150,000. Pre-money valuation: £600,000. Post-money valuation: £750,000. Equity offered: 20% across the fully subscribed round.",
          "These are proposed terms for a round in preparation — no investment has been secured, and the round has not been completed.",
        ],
      },
      {
        heading: "Where the money goes",
        paragraphs: [
          "The planned allocation is: £85,000 (56.7%) to MVP and AI development and directly related R&D; £35,000 (23.3%) to go-to-market and controlled acquisition; £20,000 (13.3%) to infrastructure, legal, compliance and operations; and £10,000 (6.7%) to working capital — £150,000 in total.",
          "The largest share is aimed at the product itself, because an impressive technology that is unpleasant to use is not a product. Humanoid robotics is explicitly not part of this round; it belongs to the company's longer-term vision and would be explored only at a later stage, subject to separate planning and funding.",
        ],
      },
      {
        heading: "What we are not claiming",
        paragraphs: [
          "No investment has been secured, and the round has not been completed. Registering interest through this website is an expression of interest only: it is not an investment, a share subscription or a binding commitment, and no investment can be made through this website.",
          "This website is provided for information purposes only and does not constitute an offer of securities or investment advice.",
          "Any actual investment would be subject to investor eligibility, appropriate legal documentation, company approval and applicable UK requirements. And it should be said plainly: capital is at risk. Investments in early-stage companies are high risk and illiquid, and investors may lose some or all of the amount invested.",
        ],
      },
      {
        heading: "Who we are speaking to",
        paragraphs: [
          "We welcome expressions of interest from angel investors, private investors, venture capital and family offices, as well as other aligned backers. The website's investment pages describe indicative interest levels and the information we ask for; none of it creates any obligation on either side.",
        ],
      },
      {
        heading: "What investors can expect from us",
        paragraphs: [
          "Precise language, honest statuses and a clear separation between what is in development and what is planned. The same discipline applied to the product pages applies here: if something has not happened yet, we say it has not happened yet — and when it does, the update will be easy to find.",
        ],
      },
      {
        heading: "Crowdcube preparation",
        paragraphs: [
          "As part of the wider investment strategy, AIROBOTCAMSTUDIO LTD is preparing for a future Crowdcube fundraising campaign. That campaign is not live and has not been approved; preparation means exactly that.",
        ],
      },
      {
        heading: "How to register interest",
        paragraphs: [
          "Prospective investors can register interest through the investor forms on this website, and will be contacted as the preparation progresses. You can also read about our SEIS Advance Assurance preparation in a separate Journal article.",
        ],
      },
    ],
  },

  "/updates/understanding-our-seis-advance-assurance-preparation": {
    title: "Understanding Our SEIS Advance Assurance Preparation",
    metaTitle: "Understanding Our SEIS Advance Assurance Preparation — AIROBOTCAMSTUDIO Journal",
    metaDesc: "What SEIS Advance Assurance is, where the AIROBOTCAMSTUDIO application stands (in preparation, not granted), and what it means for investors.",
    date: "29 August 2026",
    readTime: "6 min read",
    related: [
      ["Preparing AIROBOTCAMSTUDIO for Investment", "/updates/preparing-airobotcamstudio-for-investment"],
      ["SEIS Advance Assurance — Investment", "/investment/seis-advance-assurance"],
      ["Our Roadmap to Interactive AI Entertainment", "/updates/our-roadmap-to-interactive-ai-entertainment"],
    ],
    intro:
      "AIROBOTCAMSTUDIO LTD is preparing an application for SEIS Advance Assurance. This article explains what that is, what our current status is, and — just as importantly — what we are not saying.",
    sections: [
      {
        heading: "What SEIS Advance Assurance is",
        paragraphs: [
          "The Seed Enterprise Investment Scheme is a UK government programme that offers tax reliefs to investors in qualifying early-stage companies. Advance Assurance is a process through which HMRC indicates, before investment, whether a company is likely to qualify — giving prospective investors more confidence about the company's position.",
          "It is an indication, not a guarantee, and final eligibility for relief always depends on the company and the individual investor satisfying HMRC requirements at the relevant time.",
        ],
      },
      {
        heading: "Where we stand",
        paragraphs: [
          "Our status is simple: the application is being prepared. AIROBOTCAMSTUDIO LTD has not been granted SEIS Advance Assurance, and HMRC has not approved the company for SEIS.",
          "Preparation means assembling the documentation HMRC expects and describing the company's plans accurately — no shortcuts, and no premature celebration.",
          "We will update this website if and when there is verified news to share.",
        ],
      },
      {
        heading: "What it means for investors",
        paragraphs: [
          "SEIS eligibility and investor tax relief are subject to HMRC approval and applicable requirements. No one should invest on the assumption that SEIS relief will apply, and nothing on this website should be read as a promise of tax relief, returns or protection of capital.",
          "As with any early-stage investment, capital is at risk and investments are illiquid. Investors may lose some or all of the amount invested, and should take independent professional advice before making any decision.",
        ],
      },
      {
        heading: "A note on language",
        paragraphs: [
          "You will notice that we always say 'preparing an application' — never 'approved', 'granted' or 'guaranteed'. That precision is intentional. SEIS is meaningful to early-stage investors precisely because the rules are strict, and describing our position accurately is part of being a company investors can trust.",
        ],
      },
      {
        heading: "Commonly confused points",
        paragraphs: [
          "Advance Assurance is not the relief itself — it is an indication about the company before investment. Company eligibility is not investor eligibility — each investor's circumstances are assessed separately. And neither is permanent — both depend on requirements continuing to be met. Keeping these distinctions straight protects investors from false expectations.",
          "If any of this is unclear, ask us before registering interest — the contact details are on this website, and we would rather answer questions early.",
        ],
      },
      {
        heading: "The proposed round it would support",
        paragraphs: [
          "The SEIS application relates to the proposed £150,000 round for AIROBOTCAMSTUDIO LTD — £600,000 pre-money, £750,000 post-money, 20% equity across the fully subscribed round — focused on completing and validating Level 1, the digital AI video companion platform. It does not relate to humanoid robotics, and it does not involve the separately registered US entity, AI ROBOT CAM STUDIO LLC, which is currently non-trading.",
          "SEIS exists to help qualifying early-stage UK companies raise investment by offering tax reliefs to eligible investors. That context matters, but it changes nothing about our discipline: application in preparation, subject to HMRC approval.",
        ],
      },
      {
        heading: "What happens next",
        paragraphs: [
          "When the application has been submitted, we will say so. If Advance Assurance is granted, we will say that too — and not before. In the meantime, prospective investors can register their interest through this website and follow progress in the Journal.",
          "If you are considering registering interest, the investor form on this website is the right place to start — an expression of interest only, with no obligation on either side.",
          "Precise wording may feel cautious. We think it is simply respectful of investors' time.",
        ],
      },
    ],
  },
};
